import { Link } from 'react-router-dom';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1,
} from '@fluentui/react-components';

// Customer-facing reports index. Visible to every signed-in user — Guests see only the
// tiles for reports their mapped projects support. Strict customer-language only on
// this page: no "WSJF", no "Backlog", no internal jargon.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    transition: 'box-shadow 0.15s ease',
    '&:hover': { boxShadow: tokens.shadow8 },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  tileDisabled: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    pointerEvents: 'none',
  },
  badge: { color: tokens.colorNeutralForeground3 },
});

interface ReportEntry {
  id: string;
  to: string | null;
  title: string;
  description: string;
}

const REPORTS: ReportEntry[] = [
  { id: 'roadmap',              to: '/reports/customer/roadmap',           title: 'See your roadmap', description: 'A clean quarterly roadmap of what is coming, in progress, and delivered.' },
  { id: 'progress',             to: '/reports/customer/progress',          title: "See your project's progress", description: 'A simple Done / Underway / Next view per project.' },
  { id: 'upcoming',             to: '/reports/customer/upcoming',          title: "See what's coming next", description: 'Upcoming deliverables in the next six weeks.' },
  { id: 'decisions',            to: '/reports/customer/decisions-risks',   title: 'See open decisions and risks', description: 'Decisions we need from you, and risks affecting your projects.' },
  { id: 'signoff',              to: '/reports/customer/signoff-queue',     title: 'Sign off completed work',      description: 'Initiatives that have completed UAT and are awaiting your sign-off.' },
];

export function CustomerReportsIndex() {
  const styles = useStyles();

  return (
    <div className={styles.page}>
      <Title2>Your Reports</Title2>
      <Body1 className={styles.hint}>
        Everything you need to see how your projects are tracking — in business language, refreshed live from Azure DevOps.
      </Body1>
      <div className={styles.grid}>
        {REPORTS.map((r) => (
          r.to ? (
            <Link key={r.id} to={r.to} className={styles.tile} aria-label={r.title}>
              <Title3>{r.title}</Title3>
              <Body1>{r.description}</Body1>
            </Link>
          ) : (
            <div key={r.id} className={`${styles.tile} ${styles.tileDisabled}`} aria-label={`${r.title} — coming soon`}>
              <Title3>{r.title}</Title3>
              <Body1>{r.description}</Body1>
              <Caption1 className={styles.badge}>Coming soon</Caption1>
            </div>
          )
        ))}
      </div>
    </div>
  );
}
